import { loadCartPna } from "../../cart/cart/pna/cartPna";
import { useEffect, useRef, useState } from "react";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import classNames from "classnames";
import { Card } from "~/features/configurator/Linear/components/Card";
import { Controller, useFormContext } from "react-hook-form";
import Parameter from "~/common/components/Parameter";
import PriceReferenceDropdown from "~/features/productDetails/components/AddToCartCard/PriceReferenceDropdown";
import { ActionIconButton } from "~/common/components/Button/ActionIconButton";
import { type PredifinedListProductData, type QuickEntryForm, type QuickEntryItem } from "../types/models";
import { isDateInvalidForDelivery } from "~/common/utils/dateUtils";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn, canSeeStaticPriceRefColumn } from "../utils/helpers";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import dayjs from "dayjs";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  AutosuggestProductInput,
  isSameProduct,
  useFetchProductAutosuggest,
} from "~/common/components/AutosuggestProductInput/AutosuggestProductInput";
import { type SrpProductData } from "~/utils/models";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { type ProductData } from "~/domains/product/types/ProductData";
import { UndoButton } from "~/common/components/Button/UndoButton";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import useMediaQuery, { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { TextField } from "~/common/mui";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { Alert } from "~/common/mui/Alert/Alert";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import { MUIDateInput } from "~/common/components/MUIDateInput";

interface RowProps extends DivAttributes {
  index: number;
  item: QuickEntryItem;
  onRemove: () => void;
  onRowCompleted?: () => void;
}

// TODO: Fix all the @ts-ignore in here

export const QuickEntryTableRow = ({ index, className, item, onRemove, onRowCompleted, ...rest }: RowProps) => {
  const { t } = useTranslation();
  const {
    watch,
    control,
    setValue,
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext<QuickEntryForm>();
  const [isUpdatedWithSuccessor, setUpdatedWithSuccessor] = useState(false);
  const [successorProduct, setSuccessorProduct] = useState<ProductData | null>(null);
  const { selection } = usePriceReferenceSelection();

  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const isPhone = !isMinTablet;

  const formNamePrefix = `items.${index}`;
  const getFieldName = (name: keyof QuickEntryItem) => formNamePrefix + "." + name;
  const watchField = <T extends keyof QuickEntryItem>(name: T): QuickEntryItem[T] => {
    // @ts-ignore
    return watch(getFieldName(name)) as QuickEntryItem[T];
  };
  const getError = (name: keyof QuickEntryItem) => errors.items?.[index]?.[name] ?? undefined;

  const fromSuccessorClick = useRef(false);

  useOnMount(() => {
    // @ts-ignore
    register(getFieldName("product"));
    // @ts-ignore
    register(getFieldName("packCode"));
    // @ts-ignore
    register(getFieldName("productId"));
    // @ts-ignore
    register(getFieldName("previousProductId"));
  });

  const isCommentVisible = watchField("isCommentVisible");
  const comment = watchField("comment");
  const priceReference = watchField("priceReference");
  const product = watchField("product");
  const productId = watchField("productId");
  const previousProductId = watchField("previousProductId");

  const productCode = product?.code;
  // Basic data arrives with the autosuggest selection; PnA loads through the canonical
  // queries (fire-and-forget like the legacy thunk dispatch).
  useEffect(() => {
    if (productCode) {
      void loadCartPna({ productCode, priceReference }).catch(() => undefined);
    }
  }, [productCode, priceReference]);

  const values = item;

  const commentButtonIcon = isCommentVisible ? <CloseIcon /> : comment ? <ModeCommentIcon /> : <CommentIcon />;

  const canMakeComments = product ? canUseCommentsForProduct(product.marketType) : false;
  const quantityVisible = () => {
    if (!product) {
      return true;
    }
    return product ? product.quantityVisible : false;
  };

  const qtyError = getError("quantity");
  const productError = getError("product");
  const dateError = getError("requestedDeliveryDate");

  const { inputProps, products, selectedProduct, packCode } = useFetchProductAutosuggest(productId ?? "");

  const refreshProductError = (product?: SrpProductData) => {
    if (product) {
      // @ts-ignore
      clearErrors(formNamePrefix);
    } else {
      // @ts-ignore
      setError(getFieldName("product"), { type: "required" });
    }
  };

  useEffect(() => {
    let productToUpdate: SrpProductData = selectedProduct as SrpProductData;
    if (
      selectedProduct &&
      isProductPMA(fakeDescriptorFromProduct(selectedProduct)) &&
      selectedProduct.successorProduct
    ) {
      productToUpdate = selectedProduct.successorProduct as SrpProductData;
      setUpdatedWithSuccessor(true);
      setSuccessorProduct(productToUpdate);
      if (packCode) {
        // @ts-ignore
        setValue(getFieldName("productId"), productToUpdate!.sapMaterialNumber);
      } else {
        // @ts-ignore
        setValue(getFieldName("productId"), productToUpdate!.designation);
      }
    } else if (!successorProduct || successorProduct.designation !== selectedProduct?.designation) {
      setUpdatedWithSuccessor(false);
      setSuccessorProduct(null);
    }

    if (fromSuccessorClick.current) {
      fromSuccessorClick.current = false;
    } else {
      // @ts-ignore
      setValue(getFieldName("previousProductId"), "");
    }

    // @ts-ignore
    setValue(getFieldName("product"), productToUpdate);

    if (productError) {
      refreshProductError(productToUpdate);
    }
  }, [selectedProduct?.code]);

  useEffect(() => {
    // @ts-ignore
    setValue(getFieldName("packCode"), packCode);
  }, [packCode]);

  const staticPriceRef = canSeeStaticPriceRefColumn(selection);

  const isTablet = useIsMinTabletSize();

  const isUserCustomer = userIsCustomer();

  return (
    <div className={classNames("scha__bulk-preview__table__row", className)} {...rest} data-test-id={"quick-entry-row"}>
      {!isPhone && (
        <div className="scha__bulk-preview__table__left-side">
          <div className={"px-3 mt-3"}>{index + 1}</div>
        </div>
      )}
      <div className="scha__bulk-preview__table__right-side">
        <Card disableHover={true} className={"py-3"}>
          <div
            className={classNames(
              "px-0 scha__bulk-preview__table__right-side__col-container",
              (!isUserCustomer || !isTablet) && "setFullWidthOnDivs",
            )}
          >
            <div>
              <AutosuggestProductInput
                {...inputProps}
                label={t("bulkUpload.table.header.productId")}
                inputValue={productId ?? ""}
                onInputChange={(event, value, reason) => {
                  if (reason !== "reset") {
                    // @ts-ignore
                    setValue(getFieldName("productId"), value);
                  }
                }}
                onChange={(event, value) => {
                  if (!isSameProduct(value ?? "", selectedProduct)) {
                    // @ts-ignore
                    setValue(getFieldName("productId"), value);
                  }
                  refreshProductError(selectedProduct);
                }}
                onBlur={() => {
                  refreshProductError(selectedProduct);
                }}
                products={products}
                disableNewVerLink={true}
              />
            </div>
            <div>
              <Controller
                render={({ field: { value, onChange, onBlur } }) =>
                  isUserCustomer ? (
                    <CustomNumberFormat
                      hidden={!quantityVisible()}
                      data-test-id={"quick-entry-item-quantity"}
                      label={t("cartPage.table.header.quantity.title")}
                      handleState={(state) => onChange(state.floatValue)}
                      error={!!qtyError}
                      // @ts-ignore
                      value={value}
                      onBlur={onBlur}
                    />
                  ) : (
                    <QuantityInput
                      hidden={!quantityVisible()}
                      data-test-id={"quick-entry-item-quantity"}
                      label={t("cartPage.table.header.quantity.title")}
                      onValueChange={onChange}
                      step={1}
                      min={1}
                      error={!!qtyError}
                      // @ts-ignore
                      value={value}
                      onBlur={onBlur}
                    />
                  )
                }
                // @ts-ignore
                name={getFieldName("quantity")}
                control={control}
                defaultValue={values.quantity}
                // @ts-ignore
                rules={VALIDATE_QTY}
              />
              {!quantityVisible() && <Parameter label={t("input.quantity")} value={values.quantity} />}
            </div>
            {isUserCustomer && (
              <div>
                <Controller
                  render={({ field: { value, onChange, onBlur } }) => (
                    <MUIDateInput
                      label={t("cartPage.table.content.chooseDate")}
                      onChange={onChange}
                      onBlur={onBlur}
                      data-test-id={"quick-entry-item-date-selector-rdd"}
                      // @ts-ignore
                      date={value}
                      isDayDisabled={isDateInvalidForDelivery}
                    />
                  )}
                  // @ts-ignore
                  name={getFieldName("requestedDeliveryDate")}
                  control={control}
                  // @ts-ignore
                  defaultValue={values.requestedDeliveryDate}
                  // @ts-ignore
                  rules={VALIDATE_DATE}
                />
              </div>
            )}

            {(canSeePriceRefColumn(selection) || staticPriceRef) && (
              <div hidden={staticPriceRef}>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <PriceReferenceDropdown
                      data-test-id={"quick-entry-item-price-reference"}
                      // @ts-ignore
                      selected={value}
                      selection={selection}
                      onChange={onChange}
                      size="medium"
                    />
                  )}
                  // @ts-ignore
                  name={getFieldName("priceReference")}
                  control={control}
                  defaultValue={values.priceReference}
                />
              </div>
            )}
            {staticPriceRef && (
              <div>
                <PriceReferenceParam priceReference={values.priceReference} product={null} />
              </div>
            )}

            <div className={"d-flex flex-justify-end"}>
              {canMakeComments && (
                <ActionIconButton
                  data-test-id={"quick-entry-item-btn-toggle-comment"}
                  icon={commentButtonIcon}
                  // @ts-ignore
                  onIconClick={() => setValue(formNamePrefix + ".isCommentVisible", !isCommentVisible)}
                />
              )}
              <ActionIconButton
                data-test-id={"quick-entry-item-btn-remove"}
                className={"ml-3"}
                icon={<DeleteIcon />}
                onIconClick={onRemove}
              />
            </div>
          </div>
          {canMakeComments && (
            <div className={"mt-3 px-3 scha__bulk-preview__table__comment-box"} hidden={!isCommentVisible}>
              <Controller
                // @ts-ignore
                name={getFieldName("comment")}
                control={control}
                render={({ field: { ref, ...inputProps } }) => (
                  <TextField
                    variant={"outlined"}
                    multiline
                    fullWidth
                    data-test-id={"quick-entry-item-input-comment"}
                    inputProps={{ sx: { height: "90px !important" } }}
                    inputRef={ref}
                    label={t("orderDetailsPage.table.comment")}
                    {...inputProps}
                  />
                )}
                rules={{ required: false }}
                defaultValue={values.comment}
              />
            </div>
          )}
          <div className="info-messages scha__bulk-preview__table__right-side__error-messages">
            {productError && (
              <Alert className="product-id" severity={"error"}>
                {t("bulkUpload.error.product.id.invalid")}
              </Alert>
            )}
            {qtyError && (
              <Alert className="quantity" severity={"error"}>
                {t("bulkUpload.error.product.quantity.invalid")}
              </Alert>
            )}
            {dateError && (
              <Alert className="date" severity={"info"}>
                {t("bulkUpload.error.product.rdd.invalid")}
              </Alert>
            )}
            {product && product.isPMA && <InfoProductMessagePMA />}
            {product && NewVersionMessage.isVisible(product) && (
              <NewVersionMessage
                baseProduct={product}
                disableLink={true}
                onClick={() => {
                  fromSuccessorClick.current = true;
                  // @ts-ignore
                  setValue(getFieldName("previousProductId"), productId);
                  // @ts-ignore
                  setValue(getFieldName("productId"), product.successorProduct!.designation);
                }}
              />
            )}
            {isUpdatedWithSuccessor && (
              <Alert className="updated-successor" severity={"info"}>
                {t("quickEntry.updatedWithSuccessor")}
              </Alert>
            )}
            {previousProductId && (
              <UndoButton
                onClick={() => {
                  // @ts-ignore
                  setValue(getFieldName("previousProductId"), "");
                  // @ts-ignore
                  setValue(getFieldName("productId"), previousProductId);
                }}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

const VALIDATE_PRODUCT = {
  validate: (value: PredifinedListProductData) => (value && value.product ? true : false),
};

const VALIDATE_QTY = {
  validate: (value: number) => !!value && value >= 1,
};

const VALIDATE_DATE = {
  validate: (value: Date) => dayjs(value).isValid(),
};
